/**
 * FeedbackControls — thumbs-up/thumbs-down reaction capture for a Tendril
 * assistant reply.
 *
 * Renders below a qualifying assistant message (see TendrilChatPanel, which
 * decides visibility — this component has no opinion on when it's shown).
 *
 * Feedback is IDEMPOTENT PER TURN (backend keys the row by turnIndex). This
 * component tracks the currently-selected reaction and lets the user change or
 * unselect it:
 *   - Thumbs-up (not selected): fires the feedback POST immediately (overwrite).
 *     The up thumb shows the highlighted/selected state only once the mutation
 *     resolves 2xx (no optimistic UI) — the reaction is the explicit signal.
 *   - Thumbs-down (not selected): highlights the down thumb AND opens the inline
 *     optional free-text reason prompt at the same time. The write happens on
 *     Submit / Skip (exactly one POST — overwrite). Submit with non-empty text
 *     includes `reason`; Skip (or Submit with an empty field) omits it entirely.
 *   - Clicking the currently-selected thumb again UNSELECTS it: fires the DELETE
 *     mutation and, on 2xx, returns to the neutral state (no thumb highlighted,
 *     reason form closed).
 *   - Switching from one thumb to the other overwrites; switching down→up also
 *     closes/clears the reason form.
 *   - On failure (network error, 5xx, 409 identity-unresolved) of a POST or
 *     DELETE: shows a retryable error state, never a false captured/neutral
 *     state. The user can re-click to retry.
 *
 * Reason max-length cap: enforced via the textarea's native `maxLength` AND a
 * defensive `.slice(0, REASON_MAX_LENGTH)` on the value actually sent in the
 * POST body. The backend now ALSO caps `reason` server-side
 * (FEEDBACK_REASON_MAX_LENGTH) — this client cap is belt-and-suspenders.
 *
 * Accessibility: aria-labels on both thumb buttons and the reason textarea;
 * `aria-pressed` reflects the selected reaction (including the down thumb while
 * the reason form is open).
 *
 * BEM class: .feedback-controls
 */
import React, { useState } from 'react';
import { useTendrilFeedbackMutation } from '../../hooks/mutations/useTendrilFeedbackMutation.ts';
import { useTendrilFeedbackDeleteMutation } from '../../hooks/mutations/useTendrilFeedbackDeleteMutation.ts';
import '../../styles/components/tendril.css';

const REASON_MAX_LENGTH = 2000;
const EXCERPT_MAX_LENGTH = 2000;

/**
 * Prompt label rendered above the thumb buttons, selected by the turn's
 * terminal outcome (tendril-streaming-responses Phase 3, WI-008 — pinned
 * copy, do not invent alternate wording). Defaults to the 'ok' copy when
 * `outcome` is omitted, matching every pre-Phase-3 buffered reply.
 */
const OUTCOME_PROMPTS = {
  ok: 'Was this response helpful?',
  degraded: 'Was this partial answer helpful?',
  aborted: 'This response got cut off — was it still useful?',
  error: 'This response hit an error — how did it go?',
};

/**
 * @param {{
 *   conversationId: string,
 *   turnIndex: number,
 *   assistantMessageExcerpt: string,
 *   outcome?: 'ok' | 'degraded' | 'aborted' | 'error',
 * }} props
 */
export default function FeedbackControls({ conversationId, turnIndex, assistantMessageExcerpt, outcome = 'ok' }) {
  const submitMutation = useTendrilFeedbackMutation();
  const deleteMutation = useTendrilFeedbackDeleteMutation();

  // The reaction currently shown as selected/highlighted. For 'up' this is set
  // only after the POST resolves 2xx; for 'down' it is set as soon as the user
  // picks it (so the thumb highlights alongside the reason form, pre-write).
  const [selectedReaction, setSelectedReaction] = useState(null); // 'up' | 'down' | null
  const [showReasonPrompt, setShowReasonPrompt] = useState(false);
  const [reasonText, setReasonText] = useState('');
  const [errorReaction, setErrorReaction] = useState(null); // which reaction failed, for retry copy

  const isPending = submitMutation.isPending || deleteMutation.isPending;

  const excerpt =
    typeof assistantMessageExcerpt === 'string'
      ? assistantMessageExcerpt.slice(0, EXCERPT_MAX_LENGTH)
      : assistantMessageExcerpt;

  function buildBody(reaction, reason) {
    return {
      conversationId,
      reaction,
      turnIndex,
      assistantMessageExcerpt: excerpt,
      // Defensive cap: never send a reason longer than REASON_MAX_LENGTH,
      // even if the textarea's native maxLength was bypassed (e.g. paste).
      ...(reason !== undefined && reason.length > 0 && {
        reason: reason.slice(0, REASON_MAX_LENGTH),
      }),
      ...(outcome !== undefined && { outcome }),
    };
  }

  function submitReaction(reaction, reason) {
    setErrorReaction(null);
    submitMutation.mutate(buildBody(reaction, reason), {
      onSuccess: () => {
        setSelectedReaction(reaction);
        setShowReasonPrompt(false);
        setReasonText('');
      },
      onError: () => {
        setErrorReaction(reaction);
      },
    });
  }

  function removeReaction(reaction) {
    setErrorReaction(null);
    deleteMutation.mutate(
      { conversationId, turnIndex },
      {
        onSuccess: () => {
          setSelectedReaction(null);
          setShowReasonPrompt(false);
          setReasonText('');
        },
        onError: () => {
          setErrorReaction(reaction);
        },
      },
    );
  }

  function handleThumbsUp() {
    if (isPending) return;
    if (selectedReaction === 'up') {
      // Clicking the selected thumb again → unselect.
      removeReaction('up');
      return;
    }
    // Switch to up (from down or neutral): close/clear any reason form and POST.
    setShowReasonPrompt(false);
    setReasonText('');
    submitReaction('up');
  }

  function handleThumbsDown() {
    if (isPending) return;
    if (selectedReaction === 'down') {
      // Clicking the selected thumb again → unselect.
      removeReaction('down');
      return;
    }
    // Switch to down: highlight the down thumb AND open the reason form at the
    // same time. The write happens on Submit / Skip.
    setErrorReaction(null);
    setSelectedReaction('down');
    setShowReasonPrompt(true);
  }

  function handleSubmitReason() {
    submitReaction('down', reasonText.trim());
  }

  function handleSkipReason() {
    submitReaction('down', undefined);
  }

  return (
    <div className="feedback-controls">
      <p className="feedback-controls__prompt">
        {OUTCOME_PROMPTS[outcome] ?? OUTCOME_PROMPTS.ok}
      </p>
      <div className="feedback-controls__buttons">
        <button
          type="button"
          className={[
            'feedback-controls__thumb-btn',
            'feedback-controls__thumb-btn--up',
            selectedReaction === 'up' ? 'feedback-controls__thumb-btn--selected' : '',
          ].join(' ').trim()}
          onClick={handleThumbsUp}
          aria-label="Thumbs up — good response"
          aria-pressed={selectedReaction === 'up'}
        >
          👍
        </button>
        <button
          type="button"
          className={[
            'feedback-controls__thumb-btn',
            'feedback-controls__thumb-btn--down',
            selectedReaction === 'down' ? 'feedback-controls__thumb-btn--selected' : '',
          ].join(' ').trim()}
          onClick={handleThumbsDown}
          aria-label="Thumbs down — bad response"
          aria-pressed={selectedReaction === 'down'}
        >
          👎
        </button>
      </div>

      {errorReaction && (
        <p className="feedback-controls__error" role="alert">
          Couldn't save your feedback — try again.
        </p>
      )}

      {showReasonPrompt && (
        <div className="feedback-controls__reason-prompt">
          <label className="feedback-controls__reason-label" htmlFor="feedback-controls-reason">
            If you have a second, please quickly explain why this response did not meet your expectations
          </label>
          <textarea
            id="feedback-controls-reason"
            className="feedback-controls__reason-input"
            value={reasonText}
            onChange={(e) => setReasonText(e.target.value)}
            maxLength={REASON_MAX_LENGTH}
            aria-label="Reason this response did not meet your expectations"
            disabled={isPending}
          />
          <div className="feedback-controls__reason-actions">
            <button
              type="button"
              className="feedback-controls__reason-submit-btn"
              onClick={handleSubmitReason}
              disabled={isPending}
              aria-label="Submit feedback reason"
            >
              Submit
            </button>
            <button
              type="button"
              className="feedback-controls__reason-skip-btn"
              onClick={handleSkipReason}
              disabled={isPending}
              aria-label="Skip feedback reason"
            >
              Skip
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
